<template>
  <div class="activity-list">
    <page-title-area>
      <h1>ACTIVITY</h1>
      <template #side-content>
        <v-btn text @click="expandSensors">
          <v-icon left color="primary">fa-chevron-circle-down</v-icon>
          {{ $mpt($t('button.expand_all')) }}
        </v-btn>
      </template>
    </page-title-area>
    <query-filter
      :loading="loading"
      :clearable="hasEvents() || hasFilter"
      @apply="executeQuery"
      @clear="clearQuery"
    >
      <v-row dense class="mb-4">
        <v-col>
          <v-select
            class="query-filter__input--type"
            v-model="form.sensor.type"
            :items="sensorTypeItems"
            :placeholder="$mpt($t('field.type'))"
            :disabled="hasSensorId"
            hide-details
            clearable
            outlined
            prepend-icon="location_searching"
            data-testid="sensor_type_query"
          />
        </v-col>
        <v-col>
          <autocomplete-text-field
            allow-new-values
            clearable
            v-model="form.sensor.place"
            :label="$mpt($t('field.place'))"
            :items="placeItems"
            :disabled="hasSensorId"
            icon="location_on"
            data-testid="sensor_place_query"
          />
        </v-col>
        <v-col>
          <autocomplete-text-field
            allow-new-values
            clearable
            v-model="form.sensor.name"
            :label="$mpt($t('field.sensor_name'))"
            :items="nameItems"
            icon="location_searching"
            :disabled="hasSensorId"
            data-testid="sensor_name_query"
          />
        </v-col>
        <v-col>
          <term-picker v-model="form.event.term">
            <template #activator="{ on, attrs }">
              <v-text-field
                :placeholder="$t('field.time_period')"
                hide-details
                outlined
                dense
                prepend-inner-icon="fa-calendar"
                readonly
                :value="termLabel"
                v-bind="attrs"
                v-on="on"
              />
            </template>
          </term-picker>
        </v-col>
        <v-col>
          <tag-picker v-model="form.event.tags">
            <template #activator="{ on, attrs }">
              <v-text-field
                :placeholder="$t('field.tag')"
                v-model="tagsLabel"
                prepend-inner-icon="local_offer"
                hide-details
                readonly
                outlined
                dense
                v-on="on"
                v-bind="attrs"
              />
            </template>
          </tag-picker>
        </v-col>
      </v-row>
      <div class="filter__checkbox text-md-center">
        <div class="checkbox-box">
          <v-checkbox v-model="form.event.pickup" hide-details data-testid="pickup_checkbox" />
            <div class="checkbox-box__label"><v-icon>star</v-icon><span>{{ $t('field.pickup') }}</span></div>
        </div>
        <div class="checkbox-box">
          <v-checkbox v-model="form.event.misdetection" hide-details />
          <div class="checkbox-box__label">
            <v-icon>sentiment_very_dissatisfied</v-icon>{{ $t('field.false_positives') }}
          </div>
        </div>
        <div class="checkbox-box">
          <v-checkbox v-model="form.event.exceptMisdetection" hide-details />
          <div class="checkbox-box__label">{{ $t('field.omit_false_positives') }}</div>
        </div>
        <div class="checkbox-box">
          <v-checkbox v-model="form.event.exceeded" hide-details />
          <div class="checkbox-box__label">{{ $t('field.exceeding_threshold') }}</div>
        </div>
        <div class="checkbox-box">
          <v-checkbox v-model="form.event.includeHidden" hide-details />
          <div class="checkbox-box__label"><v-icon>fa-eye-slash</v-icon>{{ $t('field.include_hidden') }}</div>
        </div>
        <div class="checkbox-box">
          <v-checkbox
            v-model="form.sensor.onlyBookmarked"
            hide-details
            data-testid="bookmarked_sensors_checkbox"
          />
          <div class="checkbox-box__label"><v-icon>mdi-bookmark</v-icon>{{ $mpt($t('field.bookmarked_sensors')) }}</div>
        </div>
        <div class="checkbox-box" v-if="featureIsActive('monitoring_point')">
          <v-checkbox
            v-model="form.sensor.includeInactiveMonitoringPoints"
            hide-details
            data-testid="include_inactive_monitoring_points_checkbox"
          />
          <div class="checkbox-box__label">{{ $t('field.include_inactive_monitoring_points') }}</div>
        </div>
      </div>
    </query-filter>
    <div class="text-right pt-4">
      <insect-count-line-charts-button :sensors="visibleSensors" :request-params="requestParams"/>
    </div>
    <template v-for="section in sensorSections">
      <div
        v-if="section.id === 'no-data'"
        :key="`${section.id}-header`"
        class="no-data-section-header py-2 px-4 font-weight-bold text-body-2"
        data-testid="no-data-section-header"
      >
        {{ section.header.label }}
      </div>
      <div
        v-else-if="section.id === 'inactive-monitoring-points'"
        :key="`${section.id}-header`"
        class="no-data-section-header py-2 px-4 font-weight-bold text-body-2"
        data-testid="inactive-monitoring-points-section-header"
      >
        {{ section.header.label }}
      </div>
      <sensor-events
        v-for="sensor in section.sensors"
        :key="sensor.id"
        :sensor="sensor"
        :requestParams="requestParams"
        :expanded="expandedSensors.includes(sensor.id)"
        @update:expanded="v => updateExpanded(sensor.id, v)"
        @event:selected="(selectedEvent, events) => showEvents(events, selectedEvent, sensor)"
        @becameVisible="() => setVisible(sensor.id)"
        @stateChange="state => onSensorStateChange(sensor.id, state)"
        ref="sensorEvents"
      />
    </template>
    <no-sensors-message v-if="!loading && sensorSections.length === 0"/>
    <gallery-view
      v-model="galleryVisible"
      :displayData="galleryData"
      :requestParams="requestParams"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  noDataSectionHeader: 'データ未受信 ({count})'
  inactiveMonitoringPointsSectionHeader: '休止モニタリングポイント ({count})'

  button:
    expand_all: すべてのセンサーを展開
    apply_filter: 読み込み
    clear_filter: クリア

  field:
    place: 設置場所
    sensor_name: センサー名
    type: センサー種別
    tag: タグ
    not_viewed: 未閲覧
    pickup: ピックアップ
    false_positives: 誤検出
    omit_false_positives: 誤検出以外
    exceeding_threshold: しきい値を超えた画像
    include_hidden: 非表示も含む
    time_period: 期間
    bookmarked_sensors: よく見るセンサー
    include_inactive_monitoring_points: 休止モニタリングポイントも含めて表示

en:
  noDataSectionHeader: 'No data received ({count})'
  inactiveMonitoringPointsSectionHeader: 'Inactive monitoring points ({count})'

  button:
    expand_all: Expand all
    apply_filter: Apply
    clear_filter: Clear

  field:
    place: Installation site
    sensor_name: Sensor name
    type: Sensor type
    tag: Tag
    not_viewed: Not viewed
    pickup: Selected
    false_positives: False positives
    omit_false_positives: Omit false positives
    exceeding_threshold: Exceeding threshold
    include_hidden: Include hidden
    time_period: Time period
    bookmarked_sensors: Bookmarked sensors
    include_inactive_monitoring_points: Include inactive monitoring points
</i18n>

<script>
import { datePeriodToTimestamps } from '@/libs/time';
import { createPromiseQueue } from '@/libs/promiseQueue';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';
import Features from '@/mixins/features';
import SensorTypes, { sensorHasCapability } from '@/mixins/sensorTypes';

import AutocompleteTextField from '@/components/atoms/AutocompleteTextField';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import QueryFilter from '@/components/molecules/QueryFilter';
import TagPicker from '@/components/molecules/TagPicker';
import GalleryView from '@/components/GalleryView/GalleryView';
import TermPicker from '@/components/TermPicker';

import { buildDefaultQuery, filterSensors, parseToValidQuery } from './queries';
import InsectCountLineChartsButton from './InsectCountLineChartsButton';
import NoSensorsMessage from './NoSensorsMessage';
import SensorEvents from './SensorEvents';
import { SENSOR_STATE } from './SensorEvents/SensorEvents';

const ACTIVITY_QUERY_STORAGE_KEY = 'activityQuery';
const matchIgnoreCase = (str, query) => str.toLowerCase().includes(query.toLowerCase());

const EVENT_LOAD_CONCURRENCY = 5;

export default {
  name: 'activity',
  mixins: [
    DateTimeFormatting,
    Features,
    SensorTypes,
  ],
  components: {
    AutocompleteTextField,
    GalleryView,
    InsectCountLineChartsButton,
    NoSensorsMessage,
    PageTitleArea,
    QueryFilter,
    SensorEvents,
    TagPicker,
    TermPicker,
  },
  computed: {
    hasFilter() {
      return !this._.isEqual(this.query, buildDefaultQuery());
    },
    hasSensorId() {
      return !!this.query.sensor.id;
    },
    // アクティビティ画面は撮影された画像・動画を見る画面のため、
    // メディアを持たないセンサーは一覧・絞り込み候補のいずれにも出さない。
    // 種別定義や hasMedia の指定が無い場合も出さない。指定漏れは「出るはずのものが
    // 消える」形で表面化させ、「出ないはずのものが黙って並ぶ」状態を作らない。
    activitySensors() {
      return this.$store.getters.getSensors.filter(
        sensor => sensorHasCapability(sensor, 'hasMedia'),
      );
    },
    availables() {
      let sensors = this.activitySensors;
      if (this.form.sensor.place) {
        sensors = sensors.filter(sensor => matchIgnoreCase(sensor.place ?? '', this.form.sensor.place));
      }
      if (this.form.sensor.name) {
        sensors = sensors.filter(sensor => matchIgnoreCase(sensor.name, this.form.sensor.name));
      }
      return sensors;
    },
    nameItems() {
      return this.availables.map(sensor => sensor.name).sorted();
    },
    placeItems() {
      return this.availables.map(sensor => sensor.place).filter(Boolean).sorted();
    },
    sensorTypeItems() {
      const sensors = this.activitySensors;
      const types = this._.uniq(sensors.map(sensor => sensor.type));
      return this._.sortBy(types.map(type => ({
        index: this.SENSOR_TYPE_INDEXES[type],
        text: this.SENSOR_TYPE_NAMES[type],
        value: type,
      })), 'index');
    },
    sensors() {
      return filterSensors(
        this.activitySensors.toArray(),
        this.query,
      );
    },
    tagsLabel() {
      return this.form.event.tags.join(', ');
    },
    termLabel() {
      const query = this.form.event;
      if (this._.isNull(query.term.from || query.term.to)) {
        return null;
      }
      const from = query.term.from ? this.formatDate(query.term.from) : '';
      const to = query.term.to ? this.formatDate(query.term.to) : '';
      return `${from} - ${to}`;
    },
    sensorSections() {
      const monitoringPointEnabled = this.featureIsActive('monitoring_point');
      const detected = [];
      const noDetection = [];
      const noData = [];
      const inactive = [];

      this.sensors.forEach(sensor => {
        if (monitoringPointEnabled && sensor.inactivated_at != null) {
          inactive.push(sensor);
          return;
        }
        const state = this.sensorStates[sensor.id];
        if (state === SENSOR_STATE.NO_DATA) {
          noData.push(sensor);
        } else if (state === SENSOR_STATE.NO_DETECTION) {
          noDetection.push(sensor);
        } else {
          detected.push(sensor);
        }
      });

      const sections = [
        { id: 'detected', sensors: detected, header: null },
        { id: 'no-detection', sensors: noDetection, header: null },
        {
          id: 'no-data',
          sensors: noData,
          header: { label: this.$t('noDataSectionHeader', { count: noData.length }) },
        },
      ];
      if (monitoringPointEnabled && this.query.sensor.includeInactiveMonitoringPoints) {
        sections.push({
          id: 'inactive-monitoring-points',
          sensors: inactive,
          header: { label: this.$t('inactiveMonitoringPointsSectionHeader', { count: inactive.length }) },
        });
      }
      return sections.filter(s => s.sensors.length > 0);
    },
    visibleSensors() {
      return this.sensors.filter(sensor =>
        this.visibleSensorIds.includes(sensor.id)
        && this.sensorStates[sensor.id] !== SENSOR_STATE.NO_DATA);
    },
  },
  created() {
    this.eventLoadQueue = createPromiseQueue(EVENT_LOAD_CONCURRENCY);
  },
  mounted: async function () {
    await this.$store.dispatch('requestSensors');

    // 最初にrestoreQueryを実行（URLパラメータがない場合のベース）
    this.restoreQuery();

    let hasUrlParams = false;

    if (this.$route.query.sensorId) {
      const sensor = this.$store.getters.getSensor(this.$route.query.sensorId);
      this.form.sensor = {
        ...this.form.sensor,
        type: sensor.type,
        name: sensor.name,
        place: sensor.place,
      };
      hasUrlParams = true;
    }

    if (this.$route.query.timePeriod) {
      try {
        const timePeriod = JSON.parse(this.$route.query.timePeriod);
        this.form.event.term = timePeriod;
        hasUrlParams = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse timePeriod from route query:', error);
      }
    }

    // URLパラメータがある場合のみ、更新処理を実行
    if (hasUrlParams) {
      this.updateQuery();
      this.storeQuery();
      await this.updateRequestParams();
    }
  },
  data() {
    return {
      form: buildDefaultQuery(),
      query: buildDefaultQuery(),
      expandedSensors: [],
      galleryData: {
        current: null,
        events: [],
        sensor: null,
      },
      galleryVisible: false,
      requestParams: {},
      loading: false,
      visibleSensorIds: [],
      sensorStates: {},
    };
  },
  methods: {
    clearEvents() {
      this.$refs.sensorEvents.forEach((s) => {
        s.clearEvents();
      });
      this.visibleSensorIds = [];
      this.sensorStates = {};
    },
    clearQuery() {
      this.form = buildDefaultQuery();
      this.query = buildDefaultQuery();
      this.$router.push({ query: {} });

      this.clearEvents();
    },
    executeQuery: async function () {
      // Ensure all query form blur events to update the form data have finished before updating
      // the query $nextTick is not enough since Vue will not give up control until this function
      // is finished
      setTimeout(() => {
        this.updateQuery();
        this.storeQuery();
        this.updateRequestParams();
      });
    },
    expandSensors() {
      this.expandedSensors = this.sensors.map(s => s.id);
    },
    getSensorEvents(sensorId, params) {
      return this.$store.dispatch('getEvents', {
        sensorId,
        params: {
          ...this.requestParams,
          ...params,
        },
      });
    },
    hasEvents() {
      const sensorEvents = this.$refs.sensorEvents || [];
      const events = this._.flatten(sensorEvents.map(sEvent => sEvent.events));
      return !this._.isEmpty(events);
    },
    onSensorStateChange(sensorId, state) {
      this.$set(this.sensorStates, sensorId, state);
    },
    setVisible(sensorId) {
      this.visibleSensorIds = [...this.visibleSensorIds, sensorId];
    },
    showEvents(events, selected, sensor) {
      this.galleryData = {
        sensor: sensor,
        events: events,
        index: events.indexOf(selected),
      };
      this.galleryVisible = true;
    },
    storeQuery() {
      const currentUser = this.$store.getters.getCurrentUser;
      const allStoredQueries = this.localObjectStorage.load(ACTIVITY_QUERY_STORAGE_KEY) || {};
      this.localObjectStorage.store(ACTIVITY_QUERY_STORAGE_KEY, {
        ...allStoredQueries,
        [currentUser.id]: this.query,
      });
    },
    restoreQuery() {
      const currentUser = this.$store.getters.getCurrentUser;
      const allStoredQueries = this.localObjectStorage.load(ACTIVITY_QUERY_STORAGE_KEY) || {};
      const storedQuery = allStoredQueries[currentUser.id] || buildDefaultQuery();
      this.query = parseToValidQuery(storedQuery);
      this.form = { ...this.query };
    },
    updateExpanded(sensorId, value) {
      if (value) {
        this.expandedSensors = [...this.expandedSensors, sensorId];
      } else {
        this.expandedSensors = this.expandedSensors.filter(id => id !== sensorId);
      }
    },
    updateRequestParams: async function () {
      this.loading = true;
      const eventQuery = this.query.event;

      const params = this._.pickBy({
        pickup: eventQuery.pickup ? '1' : null,
        exceeded: eventQuery.exceeded ? '1' : null,
        tags: eventQuery.tags.length > 0 ? JSON.stringify(eventQuery.tags) : null,
        with_hidden: eventQuery.includeHidden ? '1' : null,
        ...datePeriodToTimestamps(this.query.event.term),
      });
      if (eventQuery.misdetection) {
        params.misdetection = '1';
      } else if (eventQuery.exceptMisdetection) {
        params.misdetection = '0';
      }

      this.requestParams = params;
      this.expandedSensors = [];

      if (this.sensors.length > 0) {
        await this.$nextTick();
        this.clearEvents();
        this.eventLoadQueue.clear();

        await this.$nextTick();
        await Promise.all(this.$refs.sensorEvents.map(s =>
          this.eventLoadQueue.enqueue(() =>
            // 個別センサーのイベント取得失敗は他センサーの読み込みを阻害すべきでないため無視する
            s.loadNextEvents(4).catch(() => {}))));
      }
      this.loading = false;
    },
    updateQuery() {
      const sensorId = this.$route.query.sensorId || this.query.sensor.id;
      this.query = {
        sensor: {
          ...this.form.sensor,
          id: sensorId || null,
        },
        event: { ...this.form.event },
      };
    },
  },
  provide() {
    return {
      getSensorEvents: this.getSensorEvents,
    };
  },
  inject: [
    'localObjectStorage',
  ],
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'
.activity-list
  .checkbox-box
    display: inline-flex
    align-items: center
    margin-right: 16px

    ::v-deep .v-input--checkbox
      margin: 0
      padding: 2px 0 0

    .checkbox-box__label
      display: flex
      align-items: center

  ::v-deep .v-text-field
    background-color: white

    .v-input__slot::before,
    .v-input__slot::after
      display: none

    .v-icon
      font-size: 20px
      padding-left: 4px

  ::v-deep .checkbox-box__label .v-icon
    font-size: 20px
    margin-right: 4px

  ::v-deep .term-picker,
  ::v-deep .tag-picker
    .menu
      width: 100%

  .query-filter
    .col
      width: 0
      flex-basis: 100%
      @media #{map-get($display-breakpoints, 'md-and-up')}
        flex-basis: 0%

    .v-input
      padding-top: 0
      padding-bottom: 0

      ::v-deep label
        color: rgba(0, 0, 0, 0.38)

    &__input--type
      display: flex
      align-items: center

      ::v-deep .v-input
        &__prepend-outer
          margin-top: 0
          margin-left: 12px

        &__control
          height: 40px
          min-height: auto
          margin-left: -45px

        &__append-inner
          margin: 0
          align-self: auto

        &__slot
          min-height: 0
          padding-left: 45px !important

      ::v-deep .v-select
        &__selections
          height: 40px
          padding: 0 !important

  .no-data-section-header
    background: #d6d6d6
    color: #424242
    margin-top: 24px
</style>
